import {
  GroupItemContextMenuRenderer,
  SubGroupContextMenuRenderer,
} from '@/console/features/groups/components/group-list/group-list-renderer';
import { TResourceGroupItem } from '@/console/features/groups/components/groups.types';
import { useGroupResourceUpdatePromises } from '@/console/features/groups/hooks/use-group-resource-update-promises/use-group-resource-update-promises';
import { useGroupedResourceElements } from '@/console/features/groups/hooks/use-grouped-resources-elements/use-grouped-resources-elements';
import { DeleteAssets } from '@/shared/features/assets/delete-assets/delete-assets';
import { GroupResourceBatchActions } from '@/shared/features/groups/components/group-resource-batch-actions/group-resource-batch-actions';
import {
  TGroupsConfig,
  TResourceElements,
  TResourceUpdatePromises,
} from '@/shared/features/groups/context/groups-config-context.types';
import { GroupedUserContextMenuRenderer } from '@/shared/features/rbac/components/users-list/users-list.renderer';
import {
  useCreateResourceGroupMutation,
  useLazyReadResourceGroupNameByIdQuery,
  useReadAllResourceGroupsQuery,
  useReadResourceGroupsForSelectQuery,
  useReadResourceGroupByIdQuery,
  useReadResourceGroupNameByIdQuery,
  useReadResourceGroupNamesForRoleRowQuery,
  useReadSubGroupsForResourceGroupQuery,
  useUpdateResourceGroupMutation,
} from '@/shared/redux/api/assets/groups/enhanced-groups-api';

const useConsoleResourceUpdatePromises = (): TResourceUpdatePromises => {
  const { createResourceUpdatePromises } = useGroupResourceUpdatePromises();
  return {
    createResourceUpdatePromises: (resources, groupId) =>
      createResourceUpdatePromises(resources as TResourceGroupItem[], groupId),
  };
};

const useConsoleResourceElements = (): TResourceElements => {
  const { getIcon, getUpdatePermissionAction } = useGroupedResourceElements();
  return {
    getIcon: (typename, iconProps) =>
      getIcon(typename as TResourceGroupItem['__typename'], iconProps),
    getUpdatePermissionAction: (typename) =>
      getUpdatePermissionAction(typename as TResourceGroupItem['__typename']),
  };
};

const renderBatchActions = (selectedRaw: unknown[], onSuccess: () => void) => (
  <GroupResourceBatchActions
    selectedItems={selectedRaw as TResourceGroupItem[]}
    onSuccess={onSuccess}
  />
);

export const consoleGroupsConfig: TGroupsConfig = {
  groupRoutePath: '/groups',
  permissionActions: {
    createGroup: 'resourceGroup:createResourceGroup',
    readGroup: 'resourceGroup:readResourceGroup',
    updateGroup: 'resourceGroup:updateResourceGroup',
    deleteGroup: 'resourceGroup:deleteResourceGroup',
  },
  api: {
    useReadAllResourceGroupsQuery,
    useReadResourceGroupsForSelectQuery,
    useReadResourceGroupByIdQuery,
    useReadResourceGroupNameByIdQuery,
    useLazyReadResourceGroupNameByIdQuery,
    useReadResourceGroupNamesForRoleRowQuery,
    useReadSubGroupsForResourceGroupQuery,
    useCreateResourceGroupMutation,
    useUpdateResourceGroupMutation,
  },
  createGroupPermission: { action: 'resourceGroup:createResourceGroup' },
  useResourceUpdatePromises: useConsoleResourceUpdatePromises,
  useResourceElements: useConsoleResourceElements,
  resourceLists: {
    users: {
      rowActions: GroupedUserContextMenuRenderer,
      renderBatchActions,
    },
    roles: {
      rowActions: GroupItemContextMenuRenderer,
      renderBatchActions,
    },
    subGroups: {
      rowActions: SubGroupContextMenuRenderer,
      renderBatchActions: (selectedRaw, onSuccess) => (
        <>
          <GroupResourceBatchActions
            selectedItems={selectedRaw as TResourceGroupItem[]}
            onSuccess={onSuccess}
          />
          <DeleteAssets
            assets={selectedRaw as TResourceGroupItem[]}
            onSuccess={onSuccess}
          />
        </>
      ),
    },
  },
};
